import { useEffect, useRef, useState } from 'react';

import { objectKeysSnakeCaseToCamelCase } from 'core/utils';

interface IProps<T> {
  url: string | undefined;
  enabled?: boolean;
  eventName?: string;
  onMessage?: (data: T) => void;
}

export const useSseConnection = <T = unknown>({
  url,
  enabled = true,
  eventName,
  onMessage,
}: IProps<T>) => {
  const [lastEvent, setLastEvent] = useState<T | null>(null);
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  useEffect(() => {
    if (!url || !enabled) {
      return;
    }

    const eventSource = new EventSource(url, { withCredentials: true });

    const handleEvent = (event: MessageEvent) => {
      try {
        const rawData = JSON.parse(event.data);
        const data = objectKeysSnakeCaseToCamelCase(rawData) as T;

        setLastEvent(data);
        onMessageRef.current?.(data);
      } catch {
        console.error('[SSE] Failed to parse event data:', event.data);
      }
    };

    if (eventName) {
      eventSource.addEventListener(eventName, handleEvent);
    } else {
      eventSource.onmessage = handleEvent;
    }

    eventSource.onerror = () => {
      console.error('[SSE] Connection error, readyState:', eventSource.readyState);
    };

    return () => {
      eventSource.close();
    };
  }, [url, enabled, eventName]);

  return { lastEvent };
};
